import React, { useState, useMemo, useEffect } from 'react';
import { AbstractProgramAddDialog } from './dialogs/abstract-program-add-dialog';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { activateProgramOperation } from './operation/activate-program-operation';
import globalConfigStore from '../../../../../stores/global-config-store';
import { Column } from 'primereact/column';
import dateUtils from '../../../../../utils/date-format';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { AbstractProgram } from '../../../../../api/types/settings/studies/abstract-program';
import { Button } from 'primereact/button';
import { CreateProgram } from './componenets/create-program';
import { Major } from '../../../../../api/types/settings/studies/major';
import { deleteAbstractProgramOperation } from './operation/delete-abstract-program-operation';
import { InputText } from 'primereact/inputtext';

const ProgramsSettingsTab = observer(() => {
  const navigate = useNavigate();

  const [isAddAbstractProgramActive, setIsAddAbstractProgramActive] =
    useState(false);

  const [selectedAbstractProgram, setSelectedAbstractProgram] =
    useState<AbstractProgram | null>(null);

  const onAddButtonClicked = () => {
    setIsAddAbstractProgramActive(true);
  };

  const closeDialog = () => {
    setIsAddAbstractProgramActive(false);
  };

  const [loading, setLoading] = useState(false);

  const handleActivateProgram = async (
    startYear: number,
    abstractProgramId: number,
  ) => {
    const branchId: number | undefined =
      globalConfigStore.rootFilter.currentBranches?.id;
    if (!branchId) {
      console.error('Branch ID is not available');
      return;
    }
    //await initializeProgram(abstractProgramId);
    await activateProgramOperation(
      branchId,
      abstractProgramId,
      startYear,
      setLoading,
      navigate,
    );
  };

  const dialogStyle = {
    width: '800px',
    backgroundColor: 'white',
    padding: '8px',
    borderRadius: '8px',
  };

  const [selectedAbstractProgramId, setSelectedAbstractProgramId] = useState<
    number | null
  >(null);

  const handleApproveSelectedStartYear = (startYear: number) => {
    if (selectedAbstractProgramId !== null) {
      handleActivateProgram(startYear, selectedAbstractProgramId);
    }
  };

  const [deleteProgramId, setDeleteProgramId] = useState<number | null>(null);
  const handleDeleteProgram = async (abstractProgramId: number) => {
    await deleteAbstractProgramOperation(abstractProgramId, setDeleteProgramId);
  };

  // Search functionality
  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');

  // Debounce search input to avoid filtering on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchText]);

  // Filter programs based on debounced search text
  const filteredPrograms = useMemo(() => {
    if (!debouncedSearchText.trim()) {
      return globalSettingsStores.abstractProgram;
    }

    const searchLower = debouncedSearchText.toLowerCase().trim();

    return globalSettingsStores.abstractProgram.filter((program) => {
      // Search in program name
      if (program.programName.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search in majors
      if (
        program.majors.some((major) =>
          major.majorName.toLowerCase().includes(searchLower),
        )
      ) {
        return true;
      }

      // Search in supervisory institutions
      if (
        program.supervisory.some((institution) =>
          institution.institutionName.toLowerCase().includes(searchLower),
        )
      ) {
        return true;
      }

      // Search in creator name
      if (program.createdByName.toLowerCase().includes(searchLower)) {
        return true;
      }

      return false;
    });
  }, [debouncedSearchText, globalSettingsStores.abstractProgram]);
  return (
    <div>
      <Dialog
        header="הוספת תוכנית אבסרקטית"
        visible={isAddAbstractProgramActive}
        style={dialogStyle}
        onHide={() => {
          setIsAddAbstractProgramActive(false);
        }}
      >
        <AbstractProgramAddDialog closeDialog={closeDialog} />
      </Dialog>

      <Dialog
        header="יצירת תוכנית"
        visible={selectedAbstractProgramId !== null}
        style={{
          width: '500px',
          height: '100vh',
          minHeight: '100vh',
          borderRadius: '0 8px 8px 0', // Only right-side border radius (top-right, bottom-right)

          backgroundColor: 'white',
          padding: '8px',
          position: 'absolute',
          left: '0px', // Positions it from the left edge
          top: '0', // Aligns to the top of the viewport
          margin: '0', // Removes any margin
          transform: 'none', // Removes any transform that might affect positioning
          display: 'flex', // Using flex for better content organization
          flexDirection: 'column', // Stacks header and content vertically
        }}
        onHide={() => {
          setSelectedAbstractProgramId(null);
        }}
      >
        <CreateProgram
          loading={loading}
          approve={handleApproveSelectedStartYear}
        />
      </Dialog>

      {selectedAbstractProgram && (
        <Dialog
          header={`תוכנית אבסרקטית ${selectedAbstractProgram.programName}`}
          visible={selectedAbstractProgram !== null}
          style={dialogStyle}
          onHide={() => setSelectedAbstractProgram(null)}
        >
          <AbstractProgramAddDialog
            abstractProgram={selectedAbstractProgram}
            closeDialog={() => setSelectedAbstractProgram(null)}
          />
        </Dialog>
      )}

      <div
        style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          marginTop: '16px',
        }}
      >
        <div style={{ position: 'relative', flex: 1 }}>
          <InputText
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="חיפוש תוכניות לפי שם, מגמה או גוף מפקח..."
            style={{
              width: '100%',
              height: '30px',
              paddingLeft: '40px',
              direction: 'rtl',
            }}
          />
          <i
            className="pi pi-search"
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d',
              pointerEvents: 'none',
            }}
          />
        </div>

        <Button
          onClick={onAddButtonClicked}
          style={{ height: '30px', flexShrink: 0 }}
          label={'להוספה'}
        />
      </div>

      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        value={filteredPrograms}
        scrollable
        onRowClick={(event: DataTableRowClickEvent) => {
          setSelectedAbstractProgram(event.data as AbstractProgram);
        }}
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
      >
        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="years"
          header="מגמות"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: AbstractProgram) => (
            <div>{rowData.majors.map((item) => item.majorName).join(', ')}</div>
          )}
        />
        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="programName"
          header="שם"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="supervisory"
          header="גוף מפקח"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: AbstractProgram) => (
            <div>{rowData.supervisory.map((item) => item.institutionName)}</div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="years"
          header="מספר שנים"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="createdByName"
          header="נוצר ע״י"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="createdAt"
          header="נוצר בתאריך"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(data: AbstractProgram) => (
            <div>{dateUtils.displayFormatDate(data.createdAt)}</div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="createdAt"
          header="נוצר בתאריך"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(data: AbstractProgram) => (
            <Button
              outlined={true}
              severity={'info'}
              style={{ height: '30px' }}
              label={'יצירת תוכנית'}
              onClick={async () => setSelectedAbstractProgramId(data.id)}
            />
          )}
        />

        <Column
          style={{ textAlign: 'right' }}
          field="נשלח ע״י"
          header=""
          body={(data: Major) => (
            <Button
              icon="pi pi-trash"
              text
              severity="danger"
              tooltip={'מחיקת תוכנית'}
              loading={deleteProgramId === data.id}
              disabled={deleteProgramId !== null}
              onClick={() => handleDeleteProgram(data.id)}
            />
          )}
        />
      </DataTable>
    </div>
  );
});

export default ProgramsSettingsTab;
